<template>
  <v-dialog v-bind="$attrs" width="80vw">
    <template #activator="{ props }">
      <slot name="activator" :props="props" />
    </template>

    <template #default="{ isActive }">
      <v-card>
        <v-card-title class="d-flex">
          <span class="flex-grow-1">{{ $t("cookiePolicy") }}</span>

          <locale-switch v-if="showLanguageSelector" class="flex-0-0" :css-class="languageTextClass" />
        </v-card-title>

        <!-- overflow container instead -->
        <v-card-text class="overflow-auto">
          <template v-for="(group, groupName, index) in groupedCookies" :key="groupName">
            <h3 :class="{ 'mt-4': index > 0 }">{{ groupName }}</h3>

            <v-table>
              <thead>
                <tr>
                  <th width="25%">{{ $t("name") }}</th>
                  <th width="10%">{{ $t("type") }}</th>
                  <th width="35%">{{ $t("description") }}</th>
                  <th width="10%">{{ $t("category") }}</th>
                  <th width="15%">{{ $t("retentionPeriod") }}</th>
                  <th width="5%">{{ $t("needConsent") }}?</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="cookie in group" :key="cookie.name">
                  <td>{{ cookie.name }}</td>
                  <td>{{ cookie.type }}</td>
                  <td>{{ cookie.purpose }}</td>
                  <td>{{ t(`cookieCategories.${cookie.category}`) }}</td>
                  <td>{{ t(`cookieRetentionPeriod.${cookie.retentionPeriod}`) }}</td>
                  <td>
                    <v-icon small :icon="cookie.needConsent ? 'check' : 'close'" />
                  </td>
                </tr>
              </tbody>
            </v-table>
          </template>
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="primary"
            variant="flat"
            class="custom-hover-primary"
            @click="
              () => {
                isActive.value = false
                emit('ok')
              }
            "
            >{{ $t("ok") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>
</template>

<script setup lang="ts">
import { useTheme } from "vuetify"

const { showLanguageSelector = false } = defineProps<{ showLanguageSelector?: boolean }>()

const { t, te } = useI18n()

const theme = useTheme()
const languageTextClass = computed(() => (theme.current.value.dark ? "text-white" : "text-black"))

const emit = defineEmits<{
  (e: "ok"): void
}>()

interface Cookie {
  name: string
  group: "general" | "preferences" | "map"
  type: string
  category: "necessary" | "preferences"
  retentionPeriod: "oneMonth" | "oneYear" | "indefinite" | "session"
  needConsent: boolean
}

interface CookieTranslation {
  purpose: string
}

type LocalizedCookie = Cookie & CookieTranslation

const cookies: Array<Cookie> = [
  {
    name: "cookiesession1",
    group: "general",
    type: "Cookie",
    category: "necessary",
    retentionPeriod: "oneYear",
    needConsent: false,
  },
  {
    name: "auth_token",
    group: "general",
    type: "Cookie",
    category: "necessary",
    retentionPeriod: "oneMonth",
    needConsent: false,
  },
  {
    name: "i18n_redirected",
    group: "general",
    type: "Cookie",
    category: "preferences",
    retentionPeriod: "oneYear",
    needConsent: false,
  },
  {
    name: "firebaseLocalStorageDb",
    group: "general",
    type: "IndexedDB",
    category: "necessary",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "firebaseLocalStorage",
    group: "general",
    type: "IndexedDB",
    category: "necessary",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "loggedSession",
    group: "general",
    type: "Session storage",
    category: "necessary",
    retentionPeriod: "session",
    needConsent: false,
  },
  {
    name: "map-last-view-ol",
    group: "general",
    type: "Session storage",
    category: "preferences",
    retentionPeriod: "session",
    needConsent: false,
  },
  {
    name: "originalToken",
    group: "general",
    type: "Session storage",
    category: "necessary",
    retentionPeriod: "session",
    needConsent: false,
  },
  {
    name: "sam::recentlyVisited",
    group: "general",
    type: "Session storage",
    category: "preferences",
    retentionPeriod: "session",
    needConsent: false,
  },
  {
    name: "<id>-ticket-status-card-<type><id>-filter--created-at-year",
    group: "general",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "<id>-ticket-chart-card-<type><id>-chart-filter--created-at-year",
    group: "general",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "selected-widgets",
    group: "general",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preference::cookiePreferences",
    group: "preferences",
    type: "Local storage",
    category: "necessary",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preference::geolocationEnabled",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preference::infoCardSelection",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preference::mapVisibility",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preference::miniDrawer",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preference::preferLarge",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preference::preferRadio",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preference::showAssetComponentIcons",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preference::showAssetWidgets",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preference::showPreviousInspectionData",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preference::theme",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preference::useGoogleMaps",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preferences::isMapHidden",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preferences::isNearYouHidden",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "sam-user-preferences::isQuickActionsHidden",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "mutationsVisible:assets",
    group: "preferences",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "maps:<id>:asset-categories",
    group: "map",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "maps:showNearby",
    group: "map",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "ol-<id>:layer-duopp",
    group: "map",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "ol-<id>:layer-gis",
    group: "map",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "ol:asset-relations",
    group: "map",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "ol:layer-pdok",
    group: "map",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "ol:layer-satellite",
    group: "map",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "ol:position",
    group: "map",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "map:<id>:cities",
    group: "map",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "show-relation-lines",
    group: "map",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
  {
    name: "map-layer-<layer>",
    group: "map",
    type: "Local storage",
    category: "preferences",
    retentionPeriod: "indefinite",
    needConsent: false,
  },
]

const translatedCookies = computed<Array<LocalizedCookie>>(() =>
  cookies.map((cookie) => {
    const translationKey = `cookies.${cookie.name}`

    return {
      ...cookie,
      purpose: te(translationKey) ? t(translationKey) : "",
    }
  }),
)

const groupedCookies = computed(() =>
  translatedCookies.value.reduce(
    (groups, cookie) => {
      const group = te(`cookieGroups.${cookie.group}`) ? t(`cookieGroups.${cookie.group}`) : t("general")
      if (!groups[group]) {
        groups[group] = []
      }
      groups[group].push(cookie)
      return groups
    },
    {} as Record<string, Array<LocalizedCookie>>,
  ),
)
</script>
